import { Component, EventEmitter, forwardRef, Input, OnChanges, Output } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators, ReactiveFormsModule } from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@angular/flex-layout';
import { CampaignBanner } from 'src/app/api/api-campaigns.service.generated';
import { BannerImage, CampaignBannerImageComponent } from '../campaign-banner-image/campaign-banner-image.component';
import { ButtonComponent } from "wecheer-common";

@Component({
  selector: 'app-campaign-banner',
  templateUrl: './campaign-banner.component.html',
  styleUrls: ['./campaign-banner.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CampaignBannerComponent),
    multi: true
  }],
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    FlexLayoutModule,
    CampaignBannerImageComponent,
    ButtonComponent
]
})
export class CampaignBannerComponent implements ControlValueAccessor, OnChanges {
  @Input() banners: BannerImage[] = [];
  @Input() editable: boolean = true;
  @Input() bannerDuration: number;
  @Input() showBannerDuration: boolean = false;
  @Output() mainBannerChanged = new EventEmitter<BannerImage>();

  form = new FormGroup({
    bannerDuration: new FormControl(5, [Validators.required, Validators.min(1), Validators.max(60)]),
  });

  constructor() {
  }

  ngOnChanges() {
    this.setFormValue();
  }

  /// ControlValueAccessor-------------------
  @Input('value') _value;
  onChange: any = () => { };
  onTouched: any = () => { };
  get value(): CampaignBanner {
    return this._value;
  }
  set value(o: CampaignBanner) {
    this._value = o;

    this.setBanners(o);

    this.onChange(o);
    this.onTouched();
  }


  writeValue(obj: any): void {
    this.value = obj;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(_isDisabled: boolean): void {
  }
  /// ControlValueAccessor------------------

  notifyFormChanged() {
    const o = this.getBanners();
    this._value = o;
    this.onChange(o);
    this.onTouched();
  }

  onAdd() {
    this.banners.push(new BannerImage());
    this.notifyFormChanged();
  }

  onRemove(b: BannerImage) {
    const ix = this.banners.indexOf(b);
    this.banners.splice(ix, 1);
    if (ix === 0)
      this.mainBannerChanged.emit(undefined);

    this.notifyFormChanged();
  }

  onBannerUploaded(banner: any) {
    const ix = this.banners.indexOf(banner);
    if (ix === 0)
      this.mainBannerChanged.emit(banner);

    this.notifyFormChanged();
  }

  public setMainBanner(banner: BannerImage) {
    if (this.banners.length === 0)
      this.banners.push(new BannerImage());

    this.banners[0] = banner;

    this.notifyFormChanged();
  }

  public setBanners(banner: CampaignBanner) {
    if (!banner)
      return;

    if (banner.url1) this.banners.push(new BannerImage({ photoUrl: banner.url1 }));
    if (banner.url2) this.banners.push(new BannerImage({ photoUrl: banner.url2 }));
    if (banner.url3) this.banners.push(new BannerImage({ photoUrl: banner.url3 }));
    if (banner.url4) this.banners.push(new BannerImage({ photoUrl: banner.url4 }));
    if (banner.url5) this.banners.push(new BannerImage({ photoUrl: banner.url5 }));
    this.notifyFormChanged();
  }
  public getBanners(): CampaignBanner {
    if (this.banners.length === 0)
      return undefined

    return new CampaignBanner({
      url1: this.banners.length < 1 ? undefined : this.banners[0].photoUrl,
      key1: this.banners.length < 1 ? undefined : this.banners[0].uploadKey,

      url2: this.banners.length < 2 ? undefined : this.banners[1].photoUrl,
      key2: this.banners.length < 2 ? undefined : this.banners[1].uploadKey,

      url3: this.banners.length < 3 ? undefined : this.banners[2].photoUrl,
      key3: this.banners.length < 3 ? undefined : this.banners[2].uploadKey,

      url4: this.banners.length < 4 ? undefined : this.banners[3].photoUrl,
      key4: this.banners.length < 4 ? undefined : this.banners[3].uploadKey,

      url5: this.banners.length < 5 ? undefined : this.banners[4].photoUrl,
      key5: this.banners.length < 5 ? undefined : this.banners[4].uploadKey
    });
  }

  setFormValue() {
    this.form.patchValue({
      bannerDuration: this.bannerDuration || 5
    });
  }

  getFormValue() {
    if (this.form.invalid) {
      return undefined;
    }
    return this.form.value;
  }

  isValid() {
    return this.form.valid;
  }
}